import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { userAPI, departmentAPI, designationAPI, tenantAPI, locationAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate, capitalizeFirst, getInitials } from "../../lib/utils";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Plus, Search, MoreHorizontal, UserX, UserCheck, Loader2, KeyRound, MapPin } from "lucide-react";

const UsersPage = () => {
  const { user: currentUser, isSuperAdmin, isHRAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [managers, setManagers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [assignLocationId, setAssignLocationId] = useState("none");
  const [selectedUser, setSelectedUser] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  
  const [formData, setFormData] = useState({
    tenant_id: "",
    employee_id: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    password: "",
    date_of_joining: "",
    date_of_birth: "",
    role: "employee",
    department_id: "",
    designation_id: "",
    manager_id: "",
    location_id: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const promises = [
        userAPI.getAll(),
        departmentAPI.getAll(),
        designationAPI.getAll(),
        locationAPI.getAll(),
      ];
      
      // SuperAdmin can select tenant
      if (isSuperAdmin) {
        promises.push(tenantAPI.getAll());
      }
      
      const results = await Promise.all(promises);
      setUsers(results[0].data);
      setDepartments(results[1].data);
      setDesignations(results[2].data);
      setLocations(results[3].data);
      
      if (isSuperAdmin && results[4]) {
        setTenants(results[4].data);
      }
      
      // Set managers from users list
      const mgrs = results[0].data.filter(u => 
        u.role === "manager" || u.role === "hr_admin"
      );
      setManagers(mgrs);
      
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      const data = { ...formData };
      
      // For HR Admin, tenant_id is auto-assigned by backend
      if (!isSuperAdmin) {
        delete data.tenant_id;
      }
      
      // Clean up empty optional fields and "none" selections
      if (!data.middle_name) delete data.middle_name;
      if (!data.department_id || data.department_id === "none") delete data.department_id;
      if (!data.designation_id || data.designation_id === "none") delete data.designation_id;
      if (!data.manager_id || data.manager_id === "none") delete data.manager_id;
      if (!data.location_id || data.location_id === "none") delete data.location_id;
      
      await userAPI.create(data);
      toast.success("User created successfully");
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreateLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      tenant_id: "",
      employee_id: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      email: "",
      password: "",
      date_of_joining: "",
      date_of_birth: "",
      role: "employee",
      department_id: "",
      designation_id: "",
      manager_id: "",
      location_id: "",
    });
  };

  const openAssignLocation = (u) => {
    setSelectedUser(u);
    setAssignLocationId(u.location_id || "none");
    setShowLocationDialog(true);
  };

  const handleAssignLocation = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await userAPI.update(selectedUser.id, {
        location_id: assignLocationId === "none" ? null : assignLocationId,
      });
      toast.success("Work location updated");
      setShowLocationDialog(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update location");
    } finally {
      setActionLoading(false);
    }
  };

  const userLocations = (tenantId) =>
    isSuperAdmin ? locations.filter((l) => l.tenant_id === tenantId) : locations;

  const handleDeactivate = async (userId) => {
    setActionLoading(true);
    try {
      await userAPI.deactivate(userId);
      toast.success("User deactivated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async (userId) => {
    setActionLoading(true);
    try {
      await userAPI.activate(userId);
      toast.success("User activated");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate user");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    setActionLoading(true);
    try {
      await userAPI.resetPassword(selectedUser.id, newPassword);
      toast.success("Password reset successfully");
      setShowResetPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: { variant: "default", label: "Active", className: "bg-emerald-100 text-emerald-700" },
      inactive: { variant: "secondary", label: "Inactive", className: "bg-slate-100 text-slate-600" },
      resigned: { variant: "destructive", label: "Resigned", className: "bg-red-100 text-red-700" },
    };
    const s = variants[status] || variants.inactive;
    return <Badge className={s.className}>{s.label}</Badge>;
  };

  const filteredUsers = users.filter(
    (user) =>
      user.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.employee_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Users</h1>
          <p className="text-slate-500">Manage employees and their roles</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              {/* Tenant Selection - SuperAdmin only */}
              {isSuperAdmin && (
                <div className="form-group">
                  <Label>Tenant / Organization *</Label>
                  <Select 
                    value={formData.tenant_id} 
                    onValueChange={(value) => setFormData({ ...formData, tenant_id: value })}
                  >
                    <SelectTrigger data-testid="user-tenant-select">
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.map((tenant) => (
                        <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>
                          {tenant.company_name} ({tenant.tenant_id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Employee ID */}
              <div className="form-group">
                <Label>Employee ID *</Label>
                <Input
                  placeholder="EMP001"
                  value={formData.employee_id}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  required
                  data-testid="user-employee-id-input"
                />
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-3 gap-4">
                <div className="form-group">
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    data-testid="user-firstname-input"
                  />
                </div>
                <div className="form-group">
                  <Label>Middle Name</Label>
                  <Input
                    value={formData.middle_name}
                    onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                    data-testid="user-middlename-input"
                  />
                </div>
                <div className="form-group">
                  <Label>Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    data-testid="user-lastname-input"
                  />
                </div>
              </div>

              {/* Email and Password */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div className="form-group">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    data-testid="user-password-input"
                  />
                </div>
              </div>

              {/* Date Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Date of Joining *</Label>
                  <Input
                    type="date"
                    value={formData.date_of_joining}
                    onChange={(e) => setFormData({ ...formData, date_of_joining: e.target.value })}
                    required
                    data-testid="user-doj-input"
                  />
                </div>
                <div className="form-group">
                  <Label>Date of Birth *</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    required
                    data-testid="user-dob-input"
                  />
                </div>
              </div>

              {/* Role */}
              <div className="form-group">
                <Label>Role *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                    <SelectItem value="hr_admin">HR Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Department and Designation */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Department</Label>
                  <Select 
                    value={formData.department_id} 
                    onValueChange={(value) => setFormData({ ...formData, department_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <Label>Designation</Label>
                  <Select 
                    value={formData.designation_id} 
                    onValueChange={(value) => setFormData({ ...formData, designation_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select designation" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {designations.map((desig) => (
                        <SelectItem key={desig.id} value={desig.id}>{desig.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Reporting Manager */}
              <div className="form-group">
                <Label>Reporting Manager</Label>
                <Select 
                  value={formData.manager_id} 
                  onValueChange={(value) => setFormData({ ...formData, manager_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select reporting manager" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (HR Admin default)</SelectItem>
                    {managers.map((mgr) => (
                      <SelectItem key={mgr.id} value={mgr.id}>
                        {mgr.first_name} {mgr.last_name} ({mgr.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Work Location (geo-fence) */}
              <div className="form-group">
                <Label>Work Location (for attendance geo-fence)</Label>
                <Select
                  value={formData.location_id}
                  onValueChange={(value) => setFormData({ ...formData, location_id: value })}
                >
                  <SelectTrigger data-testid="user-location-select">
                    <SelectValue placeholder="Select work location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (any active location)</SelectItem>
                    {userLocations(formData.tenant_id).map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} ({loc.radius_meters}m)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" className="w-full btn-primary" disabled={createLoading} data-testid="submit-create-user">
                {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create User"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or employee ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="user-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Employee ID</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Work Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="avatar">
                          {getInitials(user.first_name, user.last_name)}
                        </div>
                        <span className="font-medium">{user.first_name} {user.last_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                        {user.employee_id}
                      </span>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {user.role?.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {departments.find((d) => d.id === user.department_id)?.name || "-"}
                    </TableCell>
                    <TableCell>
                      {locations.find((l) => l.id === user.location_id)?.name || "-"}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(user.status)}
                    </TableCell>
                    <TableCell>{formatDate(user.date_of_joining || user.created_at)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" disabled={user.id === currentUser?.id}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedUser(user);
                              setShowResetPasswordDialog(true);
                            }}
                          >
                            <KeyRound className="w-4 h-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => openAssignLocation(user)}
                            data-testid={`assign-location-${user.id}`}
                          >
                            <MapPin className="w-4 h-4 mr-2" />
                            Assign Work Location
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {user.status === "active" ? (
                            <DropdownMenuItem 
                              onClick={() => handleDeactivate(user.id)} 
                              className="text-red-600"
                            >
                              <UserX className="w-4 h-4 mr-2" />
                              Deactivate
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              onClick={() => handleActivate(user.id)}
                              className="text-emerald-600"
                            >
                              <UserCheck className="w-4 h-4 mr-2" />
                              Activate
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Reset password for <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong> ({selectedUser?.employee_id})
            </p>
            <div className="form-group">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                minLength={6}
                data-testid="reset-password-input"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowResetPasswordDialog(false);
                  setSelectedUser(null);
                  setNewPassword("");
                }}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 btn-primary" 
                onClick={handleResetPassword}
                disabled={actionLoading || !newPassword}
                data-testid="confirm-reset-password"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset Password"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Work Location Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Work Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Set the geo-fenced location for <strong>{selectedUser?.first_name} {selectedUser?.last_name}</strong> ({selectedUser?.employee_id}).
              Attendance check-in will be validated against this location&apos;s radius.
            </p>
            <div className="form-group">
              <Label>Work Location</Label>
              <Select value={assignLocationId} onValueChange={setAssignLocationId}>
                <SelectTrigger data-testid="assign-location-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (any active location)</SelectItem>
                  {userLocations(selectedUser?.tenant_id).map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.radius_meters}m)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setShowLocationDialog(false); setSelectedUser(null); }}>
                Cancel
              </Button>
              <Button className="flex-1 btn-primary" onClick={handleAssignLocation} disabled={actionLoading} data-testid="confirm-assign-location">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Location"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
